import { useCallback, useState } from 'react'

import EditIcon from '@mui/icons-material/Create'
import DeleteIcon from '@mui/icons-material/Delete'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { api } from '~/api'
import { SaleNote } from '~/sales/schema'
import { useAppSelector } from '~/store'
import { formatDate } from '~/utils'
import ImageList from './image-list'
import ImageManager from './image-manager'
import SaleSection from './sale-section'

type NoteProps = {
    mode: 'create' | 'update' | 'view' | 'placeholder'
    saleId: number
    storeId: number
    note?: SaleNote
}

function Note({ mode, saleId, storeId, note }: NoteProps) {
    const staticFilesDomain = useAppSelector((state) => state.initial.staticFilesDomain)

    const [currentNote, setCurrentNote] = useState<Partial<SaleNote>>(note || { sale: saleId })
    const [currentMode, setCurrentMode] = useState(mode)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

    const images = currentNote.images || []
    const disableSaveButton = !currentNote.text && images.length === 0

    const { mutate: createNote, isLoading: isCreating } = api.sales.createNote.useMutation({
        meta: {
            invalidates: [['sales', { saleId }, 'notes']],
        },
        onSuccess: () => {
            setCurrentMode('placeholder')
            setCurrentNote((prev) => ({ ...prev, text: '' }))
        },
    })

    const { mutate: updateNote, isLoading: isUpdating } = api.sales.updateNote.useMutation({
        meta: {
            invalidates: [['sales', { saleId }, 'notes']],
        },
        onSuccess: () => {
            setCurrentMode('view')
        },
    })

    const { mutate: deleteNote, isLoading: isDeleting } = api.sales.deleteNote.useMutation({
        meta: {
            invalidates: [['sales', { saleId }, 'notes']],
        },
        onSuccess: () => {
            setShowDeleteConfirm(false)
        },
    })

    const handleImageChange = useCallback((images: SaleNote['images'], callback?: () => void) => {
        setCurrentNote((prev) => ({ ...prev, images }))
        callback?.()
    }, [])

    return (
        <Box
            sx={{
                position: 'relative',
                '&:hover': {
                    '> div[aria-role=action-buttons]': {
                        display: 'block',
                    },
                },
            }}
        >
            {currentMode === 'view' && (
                <Box
                    aria-role="action-buttons"
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        display: 'none',
                    }}
                >
                    <IconButton
                        aria-label="update"
                        size="small"
                        onClick={() => setCurrentMode('update')}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={() => setShowDeleteConfirm(true)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
            {currentMode === 'view' ? (
                <>
                    {currentNote.date_created && (
                        <Typography variant="caption" color="textSecondary">
                            {formatDate(currentNote.date_created, { format: 'D MMM YYYY HH:mm' })}
                        </Typography>
                    )}
                    {currentNote.text?.split('\n').map((line, i) => (
                        <Typography variant="body2" key={i}>
                            {line}
                        </Typography>
                    ))}
                </>
            ) : (
                <TextField
                    multiline
                    fullWidth
                    placeholder={gettext('Add a note')}
                    value={currentMode === 'placeholder' ? '' : currentNote.text}
                    maxRows={5}
                    minRows={5}
                    onFocus={() => {
                        if (currentMode === 'placeholder') {
                            setCurrentMode('create')
                        }
                    }}
                    onChange={(e) => setCurrentNote((prev) => ({ ...prev, text: e.target.value }))}
                />
            )}
            {!!currentNote?.images?.length && currentMode === 'view' && (
                <ImageList images={currentNote.images} staticFilesDomain={staticFilesDomain} />
            )}
            {(currentMode === 'create' || currentMode === 'update') && (
                <Stack gap={1}>
                    <ImageManager
                        storeID={storeId}
                        images={images}
                        imagesChangedHandler={handleImageChange}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            disabled={isCreating || isUpdating}
                            size="small"
                            onClick={() => {
                                setCurrentMode(currentMode === 'create' ? 'placeholder' : 'view')
                                setCurrentNote({ ...note })
                            }}
                        >
                            {gettext('Cancel')}
                        </Button>
                        <LoadingButton
                            disableElevation
                            loading={isCreating || isUpdating}
                            size="small"
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 0.5 }}
                            disabled={disableSaveButton}
                            onClick={() => {
                                if (currentMode === 'create') {
                                    createNote({
                                        body: { ...currentNote },
                                        params: { saleId },
                                    })
                                } else if (currentNote.id) {
                                    updateNote({
                                        body: { ...currentNote },
                                        params: { saleId, noteId: currentNote.id },
                                    })
                                }
                            }}
                        >
                            {gettext('Save')}
                        </LoadingButton>
                    </Box>
                </Stack>
            )}

            <Dialog
                open={showDeleteConfirm}
                aria-labelledby="dialog-title"
                onClose={() => setShowDeleteConfirm(false)}
            >
                <DialogTitle id="dialog-title">{gettext('Confirmation')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {gettext('Do you want to delete this note?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button size="small" autoFocus onClick={() => setShowDeleteConfirm(false)}>
                        {gettext('Cancel')}
                    </Button>
                    <LoadingButton
                        loading={isDeleting}
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            if (currentNote?.id) {
                                deleteNote({ params: { saleId, noteId: currentNote.id } })
                            }
                        }}
                    >
                        {gettext('Delete')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

type Props = {
    saleId: number
    storeId: number
}

export default function Notes({ saleId, storeId }: Props) {
    const { data } = api.sales.notes.useQuery(['sales', { saleId }, 'notes'], {
        params: { saleId },
    })

    return (
        <SaleSection label={gettext('Notes')}>
            {!!data?.body.length && (
                <Stack gap={1} sx={{ color: (theme) => theme.palette.grey[700] }}>
                    {data.body.map((note) => (
                        <Note
                            key={note.id}
                            mode="view"
                            note={note}
                            saleId={saleId}
                            storeId={storeId}
                        />
                    ))}
                </Stack>
            )}

            <Note mode="placeholder" saleId={saleId} storeId={storeId} />
        </SaleSection>
    )
}
